"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Check, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import styles from "./hero-stage.module.css";

/**
 * `SITE-HOME01`: the hero's product stage (the 2026-09-24 canvas, "Learn,
 * Practice, Prove"). Three samples of the real product, each on screen for
 * 3.6 s, one pass and then it stops on Prove; a pause button stops it sooner,
 * and picking a step stops it on that step. With reduced motion it never
 * advances by itself and every animation shows its final frame
 * (`docs/03` §7, showcase motion).
 *
 * Every string arrives translated from the server, so this island carries no
 * message catalogue. Nothing in it is live: each panel says it is a sample.
 */

export type StageStep = "learn" | "practice" | "prove";

export type HeroStageCopy = {
  tabs: Record<StageStep, { name: string; what: string }>;
  tablistLabel: string;
  pause: string;
  play: string;
  caption: string;
  learn: { crumb: string; chip: string; title: string; body: string; run: string };
  practice: { crumb: string; chip: string; title: string; level: string; summary: string };
  prove: {
    crumb: string;
    chip: string;
    title: string;
    question: string;
    options: [string, string, string, string];
    log: [string, string, string];
  };
};

const STEPS: StageStep[] = ["learn", "practice", "prove"];
export const STEP_MS = 3600;

const LEARN_CODE: Array<[string, string]> = [
  ["#include <stdio.h>", "text-accent-peach"],
  ["", ""],
  ["int main(void) {", "text-neutral-200"],
  ['    printf("Hello, C.\\n");', "text-amber-200"],
  ["    return 0;", "text-neutral-200"],
  ["}", "text-neutral-200"],
];

const JUDGE_CODE = [
  "int n, q; cin >> n >> q;",
  "while (q--) {",
  "    long long a, b; cin >> a >> b;",
  "    cout << solve(a, b) << '\\n';",
  "}",
];

const TESTS = ["S1", "S2", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

/** A sitting's clock: a live countdown is a clock, not a sequence, so it keeps ticking (`docs/03` §7). */
function useExamClock(): string {
  const [left, setLeft] = useState(29 * 60 + 58);
  useEffect(() => {
    const timer = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(left / 60))}:${pad(left % 60)}`;
}

type Mode = "playing" | "paused" | "manual" | "done";

export function HeroStage({ copy }: { copy: HeroStageCopy }) {
  const [step, setStep] = useState<StageStep>("learn");
  const [mode, setMode] = useState<Mode>("playing");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseId = useId();
  const examClock = useExamClock();

  // Reduced motion: nothing advances by itself, and the bar sits full.
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) setMode("done");
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (mode !== "playing") return;
    timer.current = setTimeout(() => {
      const next = STEPS.indexOf(step) + 1;
      if (next < STEPS.length) setStep(STEPS[next]!);
      else setMode("done");
    }, STEP_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [step, mode]);

  const togglePlay = useCallback(() => {
    if (mode === "playing") {
      setMode("paused");
      return;
    }
    if (mode === "done") setStep("learn");
    setMode("playing");
  }, [mode]);

  const pick = (next: StageStep) => {
    setStep(next);
    setMode("manual");
  };

  const playing = mode === "playing";

  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div className="flex flex-col overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950 shadow-[0_30px_80px_rgba(17,17,17,0.22)]">
        <div className="flex h-14 items-stretch border-b border-neutral-800" role="tablist" aria-label={copy.tablistLabel}>
          {STEPS.map((id, index) => {
            const on = id === step;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`${baseId}-tab-${id}`}
                aria-selected={on}
                aria-controls={`${baseId}-panel`}
                tabIndex={on ? 0 : -1}
                onClick={() => pick(id)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
                  event.preventDefault();
                  const delta = event.key === "ArrowRight" ? 1 : -1;
                  const next = STEPS[(index + delta + STEPS.length) % STEPS.length]!;
                  pick(next);
                  document.getElementById(`${baseId}-tab-${next}`)?.focus();
                }}
                className={cn(
                  "relative flex min-w-0 flex-1 items-center gap-2.5 border-r border-neutral-800 px-2.5 text-left outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-4",
                  on ? "bg-neutral-900" : "hover:bg-white/[0.03]",
                )}
              >
                <span className={cn("hidden font-mono text-xs 2xl:inline", on ? "text-accent-peach" : "text-neutral-600")}>
                  0{index + 1}
                </span>
                <span className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:gap-2.5">
                  <span className={cn("whitespace-nowrap text-sm font-bold sm:text-[15px]", on ? "text-white" : "text-neutral-400")}>
                    {copy.tabs[id].name}
                  </span>
                  <span className="truncate text-[11px] text-neutral-500 sm:text-xs">{copy.tabs[id].what}</span>
                </span>
                {on ? (
                  <span
                    key={`${step}-${mode}`}
                    className={cn(styles.bar, playing ? styles.barRun : styles.barFull)}
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? copy.pause : copy.play}
            className="flex w-12 shrink-0 items-center justify-center text-neutral-300 outline-none transition-colors duration-150 ease-out hover:text-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:w-14"
          >
            {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
          </button>
        </div>

        <div
          id={`${baseId}-panel`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${step}`}
          key={step}
          className="h-[456px] overflow-hidden px-4 py-5 sm:h-[440px] sm:px-6 sm:py-6"
        >
          {step === "learn" ? <LearnPanel copy={copy.learn} /> : null}
          {step === "practice" ? <PracticePanel copy={copy.practice} /> : null}
          {step === "prove" ? <ProvePanel copy={copy.prove} clock={examClock} /> : null}
        </div>
      </div>
      <p className="pl-1 text-xs text-neutral-500">{copy.caption}</p>
    </div>
  );
}

function Crumb({ text, chip, chipTone }: { text: string; chip: string; chipTone: "green" | "grey" }) {
  return (
    <div className={cn("flex items-center justify-between gap-3", styles.rise)}>
      <span className="truncate text-xs text-neutral-400 sm:text-[13px]">{text}</span>
      <span
        className={cn(
          "inline-flex h-[26px] shrink-0 items-center rounded-full px-2.5 text-xs font-bold",
          chipTone === "green" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/[0.08] text-neutral-300",
        )}
      >
        {chip}
      </span>
    </div>
  );
}

function CodeBlock({ lines }: { lines: Array<[string, string]> }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900 py-3 font-mono text-[12.5px] leading-6 sm:text-sm sm:leading-7">
      {lines.map(([text, tone], i) => (
        <div
          key={i}
          className={cn("flex gap-4 px-4", styles.line)}
          style={{ animationDelay: `${(0.25 + i * 0.2).toFixed(2)}s` }}
        >
          <span className="w-3.5 shrink-0 text-right text-neutral-600" aria-hidden>
            {i + 1}
          </span>
          <span className={cn("whitespace-pre", tone)}>{text}</span>
        </div>
      ))}
    </div>
  );
}

function LearnPanel({ copy }: { copy: HeroStageCopy["learn"] }) {
  return (
    <div className="flex h-full flex-col gap-3 sm:gap-3.5">
      <Crumb text={copy.crumb} chip={copy.chip} chipTone="green" />
      <p className={cn("text-xl font-bold text-white sm:text-2xl", styles.rise)} style={{ animationDelay: "60ms" }}>
        {copy.title}
      </p>
      <p className={cn("text-sm leading-relaxed text-neutral-300 sm:text-[15px]", styles.rise)} style={{ animationDelay: "120ms" }}>
        {copy.body}
      </p>
      <CodeBlock lines={LEARN_CODE} />
      <div className="flex items-center gap-3">
        <span
          className={cn("inline-flex h-[38px] shrink-0 items-center gap-2 rounded-[10px] bg-primary px-4 text-sm font-bold text-white", styles.pulse)}
          style={{ animationDelay: "1.7s" }}
          aria-hidden
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          {copy.run}
        </span>
        <div
          className={cn("flex h-[38px] flex-1 items-center justify-between rounded-[10px] border border-neutral-800 bg-neutral-900 px-3.5", styles.pop)}
          style={{ animationDelay: "2s" }}
        >
          <span className="font-mono text-sm text-white">Hello, C.</span>
          <span className="font-mono text-xs text-emerald-300">exit 0 · 0.01 s</span>
        </div>
      </div>
    </div>
  );
}

function PracticePanel({ copy }: { copy: HeroStageCopy["practice"] }) {
  return (
    <div className="flex h-full flex-col gap-3 sm:gap-4">
      <Crumb text={copy.crumb} chip={copy.chip} chipTone="grey" />
      <div className={cn("flex flex-wrap items-center gap-2.5", styles.rise)} style={{ animationDelay: "60ms" }}>
        <span className="text-xl font-bold text-white sm:text-2xl">{copy.title}</span>
        <span className="inline-flex h-[26px] items-center rounded-full bg-emerald-500/15 px-2.5 text-xs font-bold text-emerald-300">
          {copy.level}
        </span>
        <span className="inline-flex h-[26px] items-center rounded-full bg-white/[0.08] px-2.5 font-mono text-xs font-bold text-neutral-300">
          C++17 · GCC 12.2.0
        </span>
      </div>
      <CodeBlock lines={JUDGE_CODE.map((line) => [line, "text-neutral-200"])} />
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
        {TESTS.map((name, i) => (
          <span
            key={name}
            className={cn("inline-flex h-[26px] items-center justify-center rounded-md font-mono text-xs font-semibold sm:h-[30px]", styles.test)}
            style={{ animationDelay: `${(1 + i * 0.12).toFixed(2)}s` }}
          >
            {name}
          </span>
        ))}
      </div>
      <div className={cn("flex flex-wrap items-center gap-3", styles.pop)} style={{ animationDelay: "2.5s" }}>
        <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 text-sm font-bold text-emerald-300">
          <Check className="h-4 w-4" aria-hidden />
          Accepted
        </span>
        <span className="font-mono text-xs text-neutral-400">{copy.summary}</span>
      </div>
    </div>
  );
}

function ProvePanel({ copy, clock }: { copy: HeroStageCopy["prove"]; clock: string }) {
  const tones = ["text-neutral-200 bg-neutral-900", "text-amber-300 bg-amber-500/10", "text-emerald-300 bg-emerald-500/10"];
  return (
    <div className="flex h-full flex-col gap-3 sm:gap-4">
      <Crumb text={copy.crumb} chip={copy.chip} chipTone="grey" />
      <div className={cn("flex items-center justify-between gap-3", styles.rise)} style={{ animationDelay: "60ms" }}>
        <span className="text-lg font-bold text-white sm:text-2xl">{copy.title}</span>
        <span className="inline-flex h-[34px] shrink-0 items-center rounded-[10px] bg-primary/15 px-3 font-mono text-[15px] font-semibold tabular-nums text-accent-peach">
          {clock}
        </span>
      </div>
      <div className={cn("flex flex-col gap-2.5 rounded-xl border border-neutral-800 bg-neutral-900 p-4", styles.rise)} style={{ animationDelay: "120ms" }}>
        <p className="text-sm leading-normal text-white sm:text-[15px]">{copy.question}</p>
        <div className="grid grid-cols-2 gap-2">
          {copy.options.map((option, i) => {
            const chosen = i === 1;
            return (
              <div
                key={option}
                className={cn(
                  "flex h-10 items-center gap-2.5 rounded-[10px] border px-3 text-sm",
                  chosen ? "border-primary bg-primary/10 text-white" : "border-neutral-700 text-neutral-300",
                )}
              >
                <span
                  className={cn("h-3.5 w-3.5 rounded-full border-2", chosen ? "border-primary bg-primary" : "border-neutral-600")}
                  aria-hidden
                />
                {option}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        {copy.log.map((entry, i) => (
          <div
            key={entry}
            className={cn("flex h-[34px] items-center gap-3 rounded-lg px-3", tones[i], styles.pop)}
            style={{ animationDelay: `${0.6 + i * 0.7}s` }}
          >
            <span className="font-mono text-xs text-neutral-400">{["10:00", "10:04", "10:05"][i]}</span>
            <span className="text-[13px] font-semibold">{entry}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
